import './home.css';
/**
 * Partner home (overview): priority action, client portfolio with health, today's work and week strip, waiting approvals,
 * next reports, wholesale pricing, white-label status and consent-only support access. Every figure comes from the same
 * endpoints the detail screens use; blocks with no real endpoint (commission ledger, referral funnel) are not drawn.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { Gauge, Heading, Panel, ScoreBar, Text, ToneChip, ToneTile, toneAt } from '@qm/ui';
import { Icon } from '../../../shell/Icon';
import { useSurface } from '../kit/context';
import { useResource, type Resource } from '../kit/resource';
import { EmptyState, Loadable, Money, Screen, When } from '../kit/ui';
import type { ApprovalView, ClientView, MonthlyReportView, WeekItem } from '../api';
import {
  HOME_HEALTH_TONE, averageHealth, bandCounts, clientsByAttention, homePriority, isOverdue, isoDay, portfolioCounts, startOfWeek, todayItems, upcomingReports, weekDays, weekStrip,
  type HomePriority,
} from '../lib/logic';
import { avatarTone, calStatus, dayOfMonth, initials, longDay, shortWeekday, timeOf, totalLeads, type CalStatus } from '../lib/views';
import { usePartnerApi } from './layout';

const CLIENT_QUERY = { tab: 'all' };
type ClientsEnvelope = { data: ClientView[]; meta?: { tabs?: Record<string, number> } };
type WeekData = { start: string; days: string[]; items: WeekItem[] };

/** Panel with a heading, a body and a footer link (the same anatomy on every home block). */
function HomePanel({ id, title, to, link, children, testId }: { id: string; title: string; to: string; link: string; children: ReactNode; testId?: string }) {
  return (
    <Panel className="qx-home__panel" aria-labelledby={id} data-testid={testId}>
      <header className="qx-home__head"><Heading level={2} id={id}>{title}</Heading></header>
      <div className="qx-home__body">{children}</div>
      <footer className="qx-home__foot"><Link to={to}>{link}<span aria-hidden="true"> {'>'}</span></Link></footer>
    </Panel>
  );
}

const CAL_TONE: Record<CalStatus, 'lavender' | 'sun' | 'mint' | 'sky' | 'coral'> = { draft: 'lavender', internal: 'sky', customer: 'sun', scheduled: 'lavender', published: 'mint' };

export function HomePage() {
  const { t, now, locale } = useSurface();
  const api = usePartnerApi();
  const today = isoDay(now());
  const start = isoDay(startOfWeek(now()));
  const clients = useResource<ClientsEnvelope>(`clients:${JSON.stringify(CLIENT_QUERY)}`, () => api.clients(CLIENT_QUERY as never) as Promise<ClientsEnvelope>);
  const approvals = useResource<ApprovalView[]>('approvals:pending', () => api.approvals({ status: 'pending' }));
  const week = useResource<WeekData>(`week:${start}`, () => api.week(start));
  const reports = useResource<MonthlyReportView[]>('reports:approved', () => api.monthlyReports({ status: 'approved' }));
  return (
    <Screen title={t('portal.partners.home.title')} eyebrow={t('portal.partners.home.welcome', { date: longDay(locale, today) })} subtitle={t('portal.partners.home.subtitle')} testId="partner-home">
      <PriorityCard clients={clients} approvals={approvals} week={week} />
      <WeekStripPanel week={week} today={today} start={start} />
      <div className="qx-home qs-split">
        <div className="qp-stack">
          <TodayPanel week={week} today={today} />
          <ClientsPanel clients={clients} />
        </div>
        <aside className="qs-aside" aria-labelledby="home-pulse" data-testid="home-pulse">
          <Heading level={2} id="home-pulse">{t('portal.partners.home.pulse')}</Heading>
          <PulseBody clients={clients} week={week} reports={reports} today={today} />
        </aside>
      </div>
      <div className="qx-home qx-home__grid">
        <div className="qx-home__col">
          <ApprovalsPanel approvals={approvals} />
          <ReportsPanel clients={clients} />
        </div>
        <div className="qx-home__col">
          <PricingPanel />
          <BrandPanel />
          <SupportPanel />
        </div>
      </div>
    </Screen>
  );
}

function PriorityCard({ clients, approvals, week }: { clients: Resource<ClientsEnvelope>; approvals: Resource<ApprovalView[]>; week: Resource<WeekData> }) {
  const { t, now } = useSurface();
  const all = [clients, approvals, week];
  if (all.some((r) => r.status === 'loading')) return <Panel className="qx-home__priority" aria-busy="true"><Text tone="muted">{t('portal.kit.loading')}</Text></Panel>;
  const rows = clients.status === 'ready' ? clients.data.data : [];
  const p = homePriority({
    approvals: approvals.status === 'ready' ? approvals.data.length : 0,
    overdue: week.status === 'ready' ? week.data.items.filter((i) => isOverdue(i, now())).length : 0,
    atRisk: bandCounts(rows).at_risk,
    onboarding: rows.filter((c) => c.status === 'onboarding').length,
  });
  // A partial failure must not read as "all clear": say nothing rather than guess.
  if (p.kind === 'clear' && all.some((r) => r.status === 'error')) return null;
  return <PriorityView p={p} />;
}

export function PriorityView({ p }: { p: HomePriority }) {
  const { t } = useSurface();
  return (
    <section className="qs-reco" aria-labelledby="home-priority" data-testid="home-priority" data-kind={p.kind}>
      <ToneTile tone={p.kind === 'clear' ? 'mint' : p.kind === 'at_risk' || p.kind === 'overdue' ? 'coral' : 'lavender'} size="md"><Icon name={p.kind === 'approvals' ? 'approvals' : p.kind === 'clear' ? 'home' : 'calendar'} size={18} /></ToneTile>
      <div className="qs-reco__copy">
        <span className="qs-micro">{t('portal.partners.home.priority')}</span>
        <Heading level={2} id="home-priority">{t(`portal.partners.home.priority.${p.kind}`, { n: p.count })}</Heading>
        <p>{t(`portal.partners.home.priority.${p.kind}.hint`)}</p>
      </div>
      <Link className="qm-button qp-reco-action" data-variant="primary" to={p.to}>{t(`portal.partners.home.priority.${p.kind}.cta`)}</Link>
    </section>
  );
}

function ClientsPanel({ clients }: { clients: Resource<ClientsEnvelope> }) {
  const { t } = useSurface();
  return (
    <HomePanel id="home-clients" title={t('portal.partners.home.clients')} to="/clients" link={t('portal.partners.home.clients.all')} testId="home-clients">
      <Loadable resource={clients}>{(env) => <ClientsBody rows={env.data} tabs={env.meta?.tabs} />}</Loadable>
    </HomePanel>
  );
}

export function ClientsBody({ rows, tabs }: { rows: readonly ClientView[]; tabs?: Record<string, number> }) {
  const { t } = useSurface();
  if (rows.length === 0) return <EmptyState message={t('portal.partners.home.clients.empty')} />;
  const counts = portfolioCounts(rows, tabs);
  const bands = bandCounts(rows);
  const list = clientsByAttention(rows).slice(0, 6);
  return (
    <>
      <dl className="qx-home__stats" aria-label={t('portal.partners.home.status')}>
        {(['active', 'onboarding', 'paused'] as const).map((k) => (
          <div key={k} data-testid={`count-${k}`}><dt>{t(`portal.partners.clientStatus.${k}`)}</dt><dd>{counts[k]}</dd></div>
        ))}
      </dl>
      <ul className="qx-home__bands" aria-label={t('portal.partners.home.bands')}>
        {(['healthy', 'watch', 'at_risk'] as const).map((b) => <li key={b}><ToneChip tone={HOME_HEALTH_TONE[b]}>{t(`portal.partners.health.${b}`)} {bands[b]}</ToneChip></li>)}
      </ul>
      <ul className="qx-home__rows" aria-label={t('portal.partners.home.clients')}>
        {list.map((c, i) => (
          <li key={c.id} className="qx-home__row" data-testid="home-client">
            <ToneTile tone={toneAt(i)} size="sm"><span>{c.name.trim().charAt(0).toUpperCase()}</span></ToneTile>
            <Link to={`/clients/${encodeURIComponent(c.id)}?tab=summary`} className="qx-home__name">{c.name}</Link>
            {c.health ? (
              <span className="qx-home__health">
                <ScoreBar value={c.health.score} tone={HOME_HEALTH_TONE[c.health.band]} label={`${c.name}: ${t('portal.partners.clients.col.health')}`} />
                <ToneChip tone={HOME_HEALTH_TONE[c.health.band]}>{t(`portal.partners.health.${c.health.band}`)} {c.health.score}</ToneChip>
              </span>
            ) : <ToneChip tone="lavender">{t(c.status === 'onboarding' ? 'portal.partners.clients.onboarding' : 'portal.partners.home.notScored')}</ToneChip>}
          </li>
        ))}
      </ul>
    </>
  );
}

function WeekStripPanel({ week, today, start }: { week: Resource<WeekData>; today: string; start: string }) {
  const { t, locale } = useSurface();
  if (week.status !== 'ready') return null;
  const days = week.data.days?.length ? week.data.days : weekDays(new Date(`${start}T00:00:00Z`));
  const strip = weekStrip(week.data.items, days, today);
  return (
    <ul className="qs-week qp-week" aria-label={t('portal.partners.calendar.title')} data-testid="home-week">
      {strip.map((d) => (
        <li key={d.day}>
          <Link className="qs-week__day" to={`/calendar?week=${start}`} data-today={d.today ? 'true' : undefined} aria-current={d.today ? 'date' : undefined} aria-label={`${longDay(locale, d.day)}: ${t('portal.partners.home.week.items', { n: d.count })}`}>
            <small>{shortWeekday(locale, d.day)}</small>
            <b>{dayOfMonth(locale, d.day)}</b>
            <small>{d.count}</small>
          </Link>
        </li>
      ))}
    </ul>
  );
}

function TodayPanel({ week, today }: { week: Resource<WeekData>; today: string }) {
  const { t } = useSurface();
  return (
    <HomePanel id="home-today" title={t('portal.partners.home.queue')} to="/calendar" link={t('portal.partners.home.today.all')} testId="home-today">
      <Loadable resource={week}>{(w) => <TodayBody items={w.items} today={today} />}</Loadable>
    </HomePanel>
  );
}

/** Today's work queue: time, customer, title, status chip and due. Built from the real calendar items due today. */
export function TodayBody({ items, today }: { items: readonly WeekItem[]; today: string }) {
  const { t, now } = useSurface();
  const due = todayItems(items, today);
  if (due.length === 0) return <Text tone="muted">{t('portal.partners.home.today.empty')}</Text>;
  return (
    <ol className="qs-queue">
      {due.map((it, i) => {
        const st = calStatus(it);
        return (
          <li key={it.id} data-testid="home-today-item">
            <span className="qs-queue__time">{timeOf(it.dueAt) || '--:--'}</span>
            <span className="qs-avatar" data-tone={avatarTone(i)} aria-hidden="true">{initials(it.clientName)}</span>
            <span className="qs-queue__title">
              <Link to={it.type === 'deliverable' ? `/deliverables/${encodeURIComponent(it.id)}` : '/calendar'}>{it.title}</Link>
              <small>{it.clientName}</small>
            </span>
            <span className="qs-queue__meta">
              <ToneChip tone={CAL_TONE[st]}>{t(`portal.partners.calstatus.${st}`)}</ToneChip>
              {isOverdue(it, now()) ? <ToneChip tone="coral">{t('portal.partners.calendar.overdue')}</ToneChip> : <span className="qp-muted">{t('portal.partners.home.queue.due')}</span>}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

/** Portfolio pulse: customer health, lead total, deliverables due and reports ready, all from the loaded resources. */
function PulseBody({ clients, week, reports, today }: { clients: Resource<ClientsEnvelope>; week: Resource<WeekData>; reports: Resource<MonthlyReportView[]>; today: string }) {
  const { t, now } = useSurface();
  return (
    <div className="qs-pulse">
      <div className="qs-pulse__block">
        <div className="qs-pulse__head"><span className="qs-micro">{t('portal.partners.home.pulse.health')}</span></div>
        <Loadable resource={clients}>
          {(env) => {
            const avg = averageHealth(env.data);
            const bands = bandCounts(env.data);
            const total = bands.at_risk + bands.watch + bands.healthy;
            if (avg === null) return <Text tone="muted">{t('portal.partners.home.pulse.noScores')}</Text>;
            return (
              <>
                <figure className="qp-pulse-gauge" data-testid="home-gauge">
                  <Gauge value={avg} label={t('portal.partners.home.gauge.label', { n: avg })} />
                  <figcaption>{t('portal.partners.home.gauge')}</figcaption>
                </figure>
                <div className="qs-ramp" aria-hidden="true">
                  {(['at_risk', 'watch', 'healthy'] as const).map((b) => <span key={b} style={{ flexGrow: bands[b], flexBasis: 0 }} />)}
                </div>
                <div className="qs-pulse__subs" data-testid="home-bands">
                  {(['at_risk', 'watch', 'healthy'] as const).map((b) => <div key={b}><b>{bands[b]}</b><small>{t(`portal.partners.health.${b}`)}</small></div>)}
                </div>
                <span className="qp-muted">{t('portal.partners.home.pulse.scored', { n: total })}</span>
              </>
            );
          }}
        </Loadable>
      </div>
      <div className="qs-pulse__block">
        <div className="qs-pulse__head"><span className="qs-micro">{t('portal.partners.home.pulse.leads')}</span></div>
        <Loadable resource={clients}>
          {(env) => (
            <>
              <span className="qs-pulse__big" data-testid="pulse-leads">{totalLeads(env.data)}</span>
              <span className="qp-muted">{t('portal.partners.home.pulse.leadsSub', { n: env.data.filter((c) => (c.leads ?? 0) > 0).length })}</span>
            </>
          )}
        </Loadable>
      </div>
      <div className="qs-pulse__block">
        <div className="qs-pulse__head"><span className="qs-micro">{t('portal.partners.home.pulse.due')}</span></div>
        <Loadable resource={week}>
          {(w) => {
            const open = w.items.filter((i) => !['published', 'cancelled', 'rejected'].includes(i.state));
            return (
              <>
                <span className="qs-pulse__big" data-testid="pulse-due">{open.length}</span>
                <div className="qs-pulse__subs">
                  <div><b>{open.filter((i) => i.day === today).length}</b><small>{t('portal.partners.home.pulse.today')}</small></div>
                  <div><b>{open.filter((i) => isOverdue(i, now())).length}</b><small>{t('portal.partners.calendar.overdue')}</small></div>
                </div>
              </>
            );
          }}
        </Loadable>
      </div>
      <div className="qs-pulse__block">
        <div className="qs-pulse__head"><span className="qs-micro">{t('portal.partners.home.pulse.reports')}</span></div>
        <Loadable resource={reports}>
          {(rows) => (
            <>
              <span className="qs-pulse__big" data-testid="pulse-reports">{rows.length}</span>
              <span className="qp-muted">{t('portal.partners.home.pulse.reportsHint')}</span>
            </>
          )}
        </Loadable>
        <Link className="qp-pulse-link" to="/reports">{t('portal.partners.home.reports.all')}</Link>
      </div>
    </div>
  );
}

function ApprovalsPanel({ approvals }: { approvals: Resource<ApprovalView[]> }) {
  const { t } = useSurface();
  return (
    <HomePanel id="home-approvals" title={t('portal.partners.home.approvals')} to="/approvals" link={t('portal.partners.home.approvals.all')} testId="home-approvals">
      <Loadable resource={approvals}>
        {(rows) => rows.length === 0 ? <Text tone="muted">{t('portal.partners.approvals.empty')}</Text> : (
          <ul className="qx-home__rows">
            {rows.slice(0, 4).map((a, i) => (
              <li key={a.id} className="qx-home__row">
                <ToneTile tone={toneAt(i)} size="sm"><Icon name="approvals" size={14} /></ToneTile>
                <span className="qx-home__what"><span>{a.title}</span><span className="qx-home__muted">{a.clientName ?? ''} <When iso={a.requestedAt} /></span></span>
              </li>
            ))}
          </ul>
        )}
      </Loadable>
    </HomePanel>
  );
}

function ReportsPanel({ clients }: { clients: Resource<ClientsEnvelope> }) {
  const { t, now } = useSurface();
  return (
    <HomePanel id="home-reports" title={t('portal.partners.home.reports')} to="/reports" link={t('portal.partners.home.reports.all')} testId="home-reports">
      <Loadable resource={clients}>
        {(env) => {
          const next = upcomingReports(env.data, now());
          return next.length === 0 ? <Text tone="muted">{t('portal.partners.home.reports.empty')}</Text> : (
            <ul className="qx-home__rows">
              {next.map((c, i) => (
                <li key={c.id} className="qx-home__row">
                  <ToneTile tone={toneAt(i)} size="sm"><Icon name="audit" size={14} /></ToneTile>
                  <span className="qx-home__what"><span>{c.name}</span><span className="qx-home__muted"><When iso={c.nextReportAt} /></span></span>
                </li>
              ))}
            </ul>
          );
        }}
      </Loadable>
    </HomePanel>
  );
}

function PricingPanel() {
  const { t } = useSurface();
  const api = usePartnerApi();
  const list = useResource('billing:wholesale', api.wholesale);
  return (
    <HomePanel id="home-pricing" title={t('portal.partners.home.pricing')} to="/pricing" link={t('portal.partners.home.pricing.all')} testId="home-pricing">
      <Loadable resource={list}>
        {(rows) => rows.length === 0 ? <Text tone="muted">{t('portal.partners.pricing.empty')}</Text> : (
          <ul className="qx-home__rows">
            {rows.slice(0, 4).map((r) => (
              <li key={r.sku} className="qx-home__row qx-home__row--price">
                <span className="qx-home__name">{r.name}</span>
                <span className="qx-home__price"><Money minor={r.wholesale.amountMinor} currency={r.wholesale.currency} /><span aria-hidden="true"> {'>'} </span><span className="qx-sr">{t('portal.partners.pricing.clientPrice')}</span><Money minor={r.clientPrice.amountMinor} currency={r.clientPrice.currency} /></span>
              </li>
            ))}
          </ul>
        )}
      </Loadable>
    </HomePanel>
  );
}

function BrandPanel() {
  const { t } = useSurface();
  const api = usePartnerApi();
  const brand = useResource('brand', api.brand);
  const domains = useResource('domains', api.domains);
  return (
    <HomePanel id="home-brand" title={t('portal.partners.home.brand')} to="/branding" link={t('portal.partners.home.brand.edit')} testId="home-brand">
      <Loadable resource={brand}>
        {(b) => (
          <ul className="qx-home__rows">
            <li className="qx-home__row"><ToneTile tone="lavender" size="sm"><Icon name="branding" size={14} /></ToneTile><span className="qx-home__what"><span>{t('portal.partners.brand.portalName')}</span><span className="qx-home__muted">{b.portalName ?? t('portal.partners.home.brand.notSet')}</span></span></li>
            <li className="qx-home__row"><ToneTile tone="sky" size="sm"><Icon name="settings" size={14} /></ToneTile><span className="qx-home__what"><span>{t('portal.partners.home.brand.quixo')}</span></span><ToneChip tone={b.hideQuixoBranding ? 'mint' : 'sun'}>{t(b.hideQuixoBranding ? 'portal.partners.home.brand.hidden' : 'portal.partners.home.brand.shown')}</ToneChip></li>
            <li className="qx-home__row"><ToneTile tone="mint" size="sm"><Icon name="search" size={14} /></ToneTile><span className="qx-home__what"><span>{t('portal.partners.brand.tab.domain')}</span>
              {domains.status === 'ready' ? <span className="qx-home__muted">{domains.data[0]?.hostname ?? t('portal.partners.domain.none')}</span> : null}</span>
              {domains.status === 'ready' && domains.data[0] ? <ToneChip tone={domains.data[0].status === 'active' ? 'mint' : domains.data[0].status === 'verification_failed' ? 'coral' : 'sun'}>{t(`portal.partners.domain.step.${domainKey(domains.data[0].status)}`)}</ToneChip> : null}</li>
          </ul>
        )}
      </Loadable>
    </HomePanel>
  );
}
const domainKey = (s: string) => (s === 'active' ? 'active' : s === 'verification_failed' ? 'failed' : s === 'pending_ssl' ? 'issuing' : 'add_records');

function SupportPanel() {
  const { t } = useSurface();
  const api = usePartnerApi();
  const grants = useResource('grants', api.accessRequests);
  return (
    <HomePanel id="home-support" title={t('portal.partners.home.support')} to="/access" link={t('portal.partners.access.request')} testId="home-support">
      <Text tone="muted">{t('portal.partners.access.subtitle')}</Text>
      <Loadable resource={grants}>
        {(rows) => rows.length === 0 ? <Text tone="muted">{t('portal.kit.empty')}</Text> : (
          <ul className="qx-home__rows">
            {rows.slice(0, 4).map((g, i) => (
              <li key={g.id} className="qx-home__row">
                <ToneTile tone={toneAt(i)} size="sm"><Icon name="team" size={14} /></ToneTile>
                <span className="qx-home__what"><span>{g.tenantName ?? g.tenantId}</span><span className="qx-home__muted"><When iso={g.createdAt} /></span></span>
                <ToneChip tone={g.status === 'active' ? 'mint' : g.status === 'requested' ? 'sun' : 'coral'}>{t(`portal.partners.access.status.${g.status}`)}</ToneChip>
              </li>
            ))}
          </ul>
        )}
      </Loadable>
    </HomePanel>
  );
}

